import React from "react";

/*
 * A component factory for polymorphic components. The `as` prop can be used to
 * render a DOM element or another React component. The component consumer will
 * get type safety with some limitations.
 *
 *  1. Components with generic props (e.g. other polymorphic components) will
 *     have their props widened to type `any`. This is a limitation of
 *     Typescript. Arguments of generic functions cannot be inferred, even if
 *     they have default values.
 *
 *     ```
 *     const Foo = Polymorphic()(({ as: Component, ...props }) => (
 *       <Component {...props} />
 *     ));
 *
 *     const Bar = Polymorphic()(({ as: Component, ...props }) => (
 *       <Component {...props} />
 *     ));
 *
 *     // All type safety is lost 😭.
 *     <Foo as={Bar} foo="foo" />
 *     ```
 *
 *  Example
 *
 *  ```
 *  const Button = Polymorphic("button")(({ as: Component, children, ...props }) => (
 *    <Component {...props}>{children}</Component>
 *  ));
 *
 *  <Button href="test" />; // Error: prop `href` does not exist on type Button
 *  <Button as="div" href="test" />; // Error
 *  <Button as="a" href="test" />; // Ok
 *  ```
 */
function polymorphic<E extends React.ElementType = "div">(el?: E) {
  return function polymorphicHOC<P = {}>(
    Component: React.ComponentType<
      Omit<P, "as"> & {
        // Allows the component author to narrow the type, this seems to work
        // despite the TypeScript error.
        // @ts-ignore
        as: "as" extends keyof P ? NonNullable<P["as"]> : React.ElementType;
      }
    >
  ) {
    const RefForwardingComponent = (
      { as = el ?? "div", ...props },
      ref: React.Ref<unknown>
    ): JSX.Element => (
      // @ts-ignore
      <Component as={as} {...props} ref={ref} />
    );

    const ForwardRefExoticComponent = React.forwardRef(RefForwardingComponent);

    // The component returned by `React.forwardRef` is not generic, in order to
    // provide type safety to the consumer we need to cast it to a generic type.
    return ForwardRefExoticComponent as <T extends React.ElementType = E>(
      props: P & { as?: T } & Omit<React.ComponentPropsWithRef<T>, "as">
    ) => JSX.Element;
  };
}

export default polymorphic;
